import { useContext, useEffect } from "react";
import { ConflictManagementContext } from "../../../../../context/ConflictManagementContext";
import { useDataContext } from "../../../../../context/DataContext";
import { SpaContext } from "../../../../../context/SpaContext";
import { getDatesBetween } from "../../../../../helpingFunctions/dateTimeUtilities";
import { generateKey } from "../../../../../helpingFunctions/helpingFunctions";
import RequestHook from "../../../../../hooks/RequestHook";
import axiosInstance from "../../../../../url/createAxios";
import { areAllConflictsResolved } from "../../../../../helpingFunctions/conflictManagement/resolveMethods/resolveMethods";
import { MAESTRO_BACKEND_NAMING_VIEWS } from "../../../../../constants/constants";
const { v4: uuidv4 } = require('uuid'); 

const SpaProcessingMethods = (serviceDetails, setIsFirstTimeBooker, setIsEditActionOnlyCancelled, setHasCancellationPolicy, setCancellationPolicyAlertsLength, setCancellationPolicyAlerts) => {
  const instance = axiosInstance();
  const { 
    setOption,
    spaSelections, 
    setSpaSelections, 
    confirmationScreenSelections,
    setConfirmationScreenSelections,
    isProcessingBookings,
    setIsProcessingBookings,
  } = useContext(SpaContext);
  const {
    setFoundConflicts,
    foundConflicts,
    prohibitRQToMaestro,
    loadNextConflict,
    setSpotlitConflict,
    replacedBookings,
  } = useContext(ConflictManagementContext);
  const {
    reservationDetails,
    setReservationDetails,
    itineraryDetails,
    isLoadingItinerary,
  } =
  useDataContext();
  const { getSpaAvailability, getItinerary, refreshReservationState } = RequestHook();

  const handleSpaBookingRQ = async (
    spaSelectionsCopy,
    skipConflictValidation
  ) => {
    // If there are any unresolved conflicts - negate the method until all of them are resolved
    if (!areAllConflictsResolved(foundConflicts) && !skipConflictValidation) {
      loadNextConflict(foundConflicts);
      return;
    }

    setSpotlitConflict(null);
    setFoundConflicts(null);
    if (prohibitRQToMaestro) {
      // Do not allow the user to proceed with booking if the found conflict is STRONG
      return;
    }
    
    setIsProcessingBookings(true);

    let conflictItemsRS = {
      data: {
        bookings: [],
      },
    };
    if (replacedBookings.length > 0) {
      conflictItemsRS = await instance.post(
        `/Hotel/Booking/Facility/Availability/ConflictReplaced`,
        {
          request: {
            spaBookings: replacedBookings.filter((row) => row.type === MAESTRO_BACKEND_NAMING_VIEWS.SPA),
            activityBookings: replacedBookings.filter(
              (row) => row.type !== MAESTRO_BACKEND_NAMING_VIEWS.SPA
            ),
          },
        },
        { withCredentials: true }
      );
    }

    const preparedItems = contructRQItems(spaSelectionsCopy);

    const hasReplacedBookingsFailures = conflictItemsRS.data.bookings.find(
      (row) => row.status !== "success"
    );

    // If the replacements have failures - auto fail all upcoming requests
    if (hasReplacedBookingsFailures) {
      for (const index in preparedItems) {
        preparedItems[index].status = "failure";
      }
      const processedRS = processRS([
        ...preparedItems,
        ...conflictItemsRS.data.bookings,
      ]);
      setConfirmationScreenSelections(processedRS);
      setIsProcessingBookings(false);
      return;
    }

    const response = await instance.post(
      `/Hotel/Booking/Facility/Availability/Spa`,
      {
        request: preparedItems,
      },
      { withCredentials: true }
    );

    if (response.data?.bookings?.some(item => item.cancellationPolicyMessage)) {
      const bookingsWithCancellationPolicy =  response.data.bookings;
      setCancellationPolicyAlerts(bookingsWithCancellationPolicy);
      setCancellationPolicyAlertsLength(bookingsWithCancellationPolicy.length);
      setIsProcessingBookings(false);
      return;
    } else {
      setHasCancellationPolicy(false);
    }


    const processedRS = processRS([
      ...conflictItemsRS.data.bookings,
      ...response.data.bookings,
    ]);
    setConfirmationScreenSelections(processedRS);
    await getItinerary();
    await refreshReservationState();
    setIsProcessingBookings(false);
    getSpaAvailability(serviceDetails.id);
  };

  const contructRQItems = (spaSelectionsCopy) => {
    const preparedRQItems = [];
    const selections = spaSelectionsCopy ? spaSelectionsCopy : spaSelections;

    selections.forEach((selection) => {
      let constructedObj = {};
      constructedObj.id = serviceDetails.id;
      constructedObj.ReservationNumber = reservationDetails.ReservationNumber;
      constructedObj.FirstName = reservationDetails.FirstName;
      constructedObj.LastName = reservationDetails.LastName;
      constructedObj.type = MAESTRO_BACKEND_NAMING_VIEWS.SPA;

      selection.on.forEach((whenSel) => {
        if (whenSel.uniqueBinder && ['add', 'remove'].includes(whenSel.action)) {
          constructedObj.uniqueBinder = whenSel.uniqueBinder;
        }

        if (whenSel.action === "edit" || selection.action === "edit") {
          const uniqueBinder = uuidv4();
          const cxlObj = { ...constructedObj };
          const cxlDate = `${whenSel.bookedDate}T${whenSel.bookedTime}`;
          cxlObj.ProviderCode = whenSel.bookedProviderCode;
          cxlObj.participantFirstName = selection.bookedFName;
          cxlObj.participantLastName = selection.bookedLName;
          cxlObj.action = "remove";
          cxlObj.isParticipant =
            reservationDetails.FirstName !== selection.bookedFName ||
            reservationDetails.LastName !== selection.bookedLName;
          cxlObj.isEditReplacement = true;
          cxlObj.replacedWith = `${whenSel.date}T${whenSel.time}`;
          cxlObj.Date = cxlDate;
          cxlObj.BookingNumber = whenSel.bookingNumber;
          cxlObj.uniqueBinder = uniqueBinder;
          if (whenSel.ReasonCode) {
            cxlObj.ReasonCode = whenSel.ReasonCode;
          }

          const bookObj = { ...constructedObj };
          const bookedDate = `${whenSel.date}T${whenSel.time}`;
          bookObj.ProviderCode = whenSel.providerCode;
          bookObj.participantFirstName = selection.fName;
          bookObj.participantLastName = selection.lName;
          bookObj.action = "add";
          bookObj.isParticipant =
            reservationDetails.FirstName !== selection.fName ||
            reservationDetails.LastName !== selection.lName;
          bookObj.Date = bookedDate;
          bookObj.isEdited = true;
          bookObj.uniqueBinder = uniqueBinder;

          cxlObj.NewDate = bookedDate;
          cxlObj.newParticipantFirstName = selection.fName;
          cxlObj.newParticipantLastName = selection.lName;
          preparedRQItems.push(cxlObj);
          preparedRQItems.push(bookObj);
        } else if (whenSel.action === "remove") {
          const cxlObj = { ...constructedObj };
          const cxlDate = `${whenSel.bookedDate ? whenSel.bookedDate : whenSel.date}T${whenSel.bookedTime ? whenSel.bookedTime : whenSel.time}`;
          cxlObj.ProviderCode = whenSel.bookedProviderCode;
          cxlObj.participantFirstName = selection.bookedFName;
          cxlObj.participantLastName = selection.bookedLName;
          cxlObj.action = whenSel.action;
          cxlObj.isParticipant =
            reservationDetails.FirstName !== selection.fName ||
            reservationDetails.LastName !== selection.lName;
          cxlObj.Date = cxlDate;
          cxlObj.BookingNumber = whenSel.bookingNumber;
          if (whenSel.ReasonCode) {
            cxlObj.ReasonCode = whenSel.ReasonCode;
          }
          preparedRQItems.push(cxlObj);
        } else {
          const date = `${whenSel.date}T${whenSel.time}`;
          preparedRQItems.push({
            ...constructedObj,
            action: whenSel.action,
            Date: date,
            ProviderCode: whenSel.providerCode,
            participantFirstName: selection.fName,
            participantLastName: selection.lName,
            isParticipant:
              reservationDetails.FirstName !== selection.fName ||
              reservationDetails.LastName !== selection.lName,
          });
        }
      });
    });

    return preparedRQItems.filter((row) => row.action !== "none");
  };

  const processRS = (responseData) => {
    const spaObjs = [];
    setIsEditActionOnlyCancelled(
      responseData.filter((row) => row.action === "remove").length ===
        responseData.length
    );
    
    responseData.forEach((sel) => {
      if (sel.action !== "none") {
        let gName = `${sel.participantFirstName} ${sel.participantLastName}`;

        if (gName.includes("undefined")) {
          gName = `${sel.FirstName} ${sel.LastName}`;
        }
        const isExistingIndex = spaObjs.findIndex(
          (row) => row.guestName === gName
        );
        
        const dateTime = sel.Date.split("T");
        if (isExistingIndex > -1) {
          spaObjs[isExistingIndex].on.push({
            date: dateTime[0],
            time: dateTime[1],
            providerCode: sel.ProviderCode,
            action: sel.action,
            Status: sel.status,
            id: sel.id,
            type: sel.type,
            isReplacement: sel.isReplacement,
            isEditReplacement: sel.isEditReplacement,
            isEdited: sel.isEdited,
            replacedWith: sel.replacedWith,
            quantity: sel.Quantity,
            bookingNumber: sel.BookingNumber,
            uniqueBinder: sel.uniqueBinder
          });
        } else {
          spaObjs.push({
            fName: sel.participantFirstName,
            lName: sel.participantLastName,
            guestName: gName,
            on: [
              {
                date: dateTime[0],
                time: dateTime[1],
                providerCode: sel.ProviderCode,
                action: sel.action,
                Status: sel.status,
                id: sel.id,
                type: sel.type,
                isEditReplacement: sel.isEditReplacement,
                isEdited: sel.isEdited,
                isReplacement: sel.isReplacement,
                replacedWith: sel.replacedWith,
                quantity: sel.Quantity,
                bookingNumber: sel.BookingNumber,
                uniqueBinder: sel.uniqueBinder
              },
            ],
            uniqueKey: generateKey(sel),
          });
        }
      }
    });

    return spaObjs;
  };

  const initialLoad = () => {
    if (itineraryDetails && serviceDetails) {
      const selections = [];
      const stayingDates = getDatesBetween(
        reservationDetails.ArrivalDate,
        reservationDetails.DepartureDate
      );

      let itinerary = [];
      stayingDates.forEach((date) => {
        const dateSpaBookings = itineraryDetails?.Dates[date]?.Spa || [];
        itinerary = [...itinerary, ...dateSpaBookings];
      });

      const selectedServiceBookings = itinerary.filter(
        (row) => row.ServiceCode === serviceDetails.ServiceCode
      );

      if (selectedServiceBookings.length > 0) {
        setIsFirstTimeBooker(false);
        const times = new Set();
        selectedServiceBookings.forEach((row) => {
          const fName = row.hasOwnProperty("ParticipantFirstName")
            ? row.ParticipantFirstName
            : row.FirstName;
          const lName = row.hasOwnProperty("ParticipantLastName")
            ? row.ParticipantLastName
            : row.LastName;
          const gName = `${fName} ${lName}`;
          const isAdded = selections.findIndex(
            (row) => row.guestName === gName
          );
          times.add(row.Time);
          if (isAdded === -1) {
            selections.push({
              fName: fName,
              lName: lName,
              isFullName: true,
              guestName: gName,
              bookedFName: fName,
              bookedLName: lName,
              spaId: serviceDetails.id,
              on: [
                {
                  date: row.Date,
                  time: row.Time,
                  bookedDate: row.Date,
                  bookedTime: row.Time,
                  action: row.action,
                  providerCode: row.ProviderCode,
                  bookedProviderCode: row.ProviderCode,
                  bookingNumber: row.BookingNumber,
                  isEdited: false,
                },
              ],
              uniqueKey: generateKey(row),
              action: row.action,
              isEdited: false,
            });
          } else {
            selections[isAdded].on.push({
              date: row.Date,
              time: row.Time,
              bookedDate: row.Date,
              bookedTime: row.Time,
              action: row.action,
              providerCode: row.ProviderCode,
              bookedProviderCode: row.ProviderCode,
              bookingNumber: row.BookingNumber,
              isEdited: false,
            });
          }
        });

        setOption(times.size === 1 ? "together" : "seperate");
      } else {
        setIsFirstTimeBooker(true);
        selections.push({
          fName: reservationDetails.FirstName,
          lName: reservationDetails.LastName,
          isFullName: true,
          bookedFName: "",
          bookedLName: "",
          guestName: `${reservationDetails.FirstName} ${reservationDetails.LastName}`,
          spaId: serviceDetails.id,
          on: [
            {
              date: "",
              time: "",
              bookedTime: "",
              bookedDate: "",
              action: "add",
              providerCode: "",
              bookedProviderCode: "",
              isEdited: false,
            },
          ],
          uniqueKey: generateKey(reservationDetails),
          action: "add",
          isEdited: false,
        });
      }

      setSpaSelections(selections);
    }
  };

  const retryFailsFunc = async () => {
    const toRetry = [];
    confirmationScreenSelections.forEach((sel) => {
      const item = JSON.parse(JSON.stringify(sel));
      item.on = [];
      sel.on.forEach((row) => {
        if (row.Status !== "success") {
          item.on.push(row);
        }
      });

      toRetry.push(item);
    });
    await handleSpaBookingRQ(toRetry, true);
  };

  useEffect(() => {
    initialLoad();
  }, [isLoadingItinerary, serviceDetails]);

  useEffect(() => {
    if (!isProcessingBookings && itineraryDetails) {
      const hasSpaBookings =
        Object.values(itineraryDetails.Dates).findIndex(
          (booking) => booking.Spa.length > 0
        ) !== -1;
      setReservationDetails((prevState) => {
        return {
          ...prevState,
          Spas: hasSpaBookings,
        };
      });
    }
  }, [isProcessingBookings]);

  // Forced trigger to try to process the bookings to maestro when the foundConflicts state is updated
  useEffect(() => {
    if (foundConflicts) {
      handleSpaBookingRQ();
    }
  }, [foundConflicts]);

  return {
    handleSpaBookingRQ,
    retryFailsFunc,
    initialLoad,
  };
};

export default SpaProcessingMethods;
